import { useEffect, useMemo, useRef, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Command,
  Download,
  FileEdit,
  FileText,
  LogOut,
  Search,
  Trash2,
  Wrench,
} from 'lucide-react';
import type { PermissionCode } from '@news-cms/shared';
import { useLogoutMutation, useSession } from '@/lib/auth';
import { ACTIVE_NAV, type IconType, flattenNavItems, navItemsFor } from '@/lib/nav-items';
import { settingsListQueryOptions } from '@/lib/settings';
import { hasPermission } from '@/lib/permissions';
import { postsListQueryOptions } from '@/lib/posts';
import { type RecentPost, loadRecentPosts } from '@/lib/recent-posts';
import { cn } from '@/lib/utils';

const apiBaseUrl = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

/**
 * Cmd+K / Ctrl+K command palette.
 *
 * Two sections, both keyboard-navigable as one unified list:
 *   1. Menu — filters ACTIVE_NAV by label + keywords (substring match)
 *   2. Posts — fetches /admin/posts?q=... when the query is ≥ 2 chars,
 *      debounced 200ms so we don't hammer D1 while the user types.
 *
 * Enter navigates: nav → its `to`, post → /posts/$id.
 *
 * Intentionally not fuzzy-ranked yet; substring match handles the
 * realistic admin workflow (typing the start of a known title/slug).
 */

/** Flat unified row — nav, post, and action entries share keyboard
 *  nav so the selection cursor moves through the whole list as one. */
type Row =
  | { kind: 'nav'; key: string; label: string; icon: IconType; to: string }
  | {
      kind: 'post';
      key: string;
      label: string;
      slug: string;
      icon: IconType;
      to: '/posts/$postId';
      params: { postId: string };
    }
  | { kind: 'action'; key: string; label: string; icon: IconType; run: () => void };

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIdx, setSelectedIdx] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Global keyboard shortcut. Use `metaKey` (Cmd) on Mac, `ctrlKey` on others
  // — both produce the same effect; modern browsers don't conflict with this
  // combo for any default action we care about.
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Reset state on open so opening twice starts fresh. Recents are
  // snapshotted at open-time (not on every render) — keeps the list
  // stable while the palette is up, refreshed on next open.
  const [recents, setRecents] = useState<RecentPost[]>([]);
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIdx(0);
      setRecents(loadRecentPosts().slice(0, 5));
      // Focus shifts after the dialog mounts; small delay lets Radix finish.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const { user } = useSession();
  const logout = useLogoutMutation();

  // Action mode: query starts with `>`. The rest after the prefix
  // becomes the action keyword filter. Plain queries continue to drive
  // the menu + post search as before.
  const actionMode = query.startsWith('>');
  const actionQuery = actionMode ? query.slice(1).trim().toLowerCase() : '';

  // Built-in actions registry. Each action lists its permission gate
  // (undefined = always visible) and a runner. Add new entries here
  // and they show up in `>actions` mode automatically.
  interface Action {
    key: string;
    label: string;
    keywords: string;
    icon: IconType;
    requires?: PermissionCode;
    requiresRole?: string;
    run: () => void;
  }
  const ACTIONS: Action[] = useMemo(
    () => [
      {
        key: 'new-post',
        label: 'เขียนข่าวใหม่',
        keywords: 'new post create เขียน สร้าง',
        icon: FileEdit,
        requires: 'post.create',
        run: () => void navigate({ to: '/posts/new' }),
      },
      {
        key: 'trash',
        label: 'เปิดถังขยะ',
        keywords: 'trash recycle bin ถังขยะ',
        icon: Trash2,
        requires: 'post.delete',
        run: () => void navigate({ to: '/posts', search: { trash: true } }),
      },
      {
        key: 'tools',
        label: 'เครื่องมือ (Tools)',
        keywords: 'tools backfill maintenance',
        icon: Wrench,
        requiresRole: 'super_admin',
        run: () => void navigate({ to: '/tools' }),
      },
      {
        key: 'audit-csv-today',
        label: 'ดาวน์โหลด Audit CSV (วันนี้)',
        keywords: 'audit csv export download',
        icon: Download,
        requires: 'audit.read',
        run: () => {
          const start = new Date();
          start.setHours(0, 0, 0, 0);
          const end = new Date(start);
          end.setDate(end.getDate() + 1);
          const params = new URLSearchParams({
            from: start.toISOString(),
            to: end.toISOString(),
          });
          window.location.href = `${apiBaseUrl}/api/admin/audit-logs/export.csv?${params.toString()}`;
        },
      },
      {
        key: 'logout',
        label: 'ออกจากระบบ',
        keywords: 'logout sign out signout',
        icon: LogOut,
        run: () => {
          logout.mutate(undefined, {
            onSuccess: () => {
              window.location.href = '/login';
            },
          });
        },
      },
    ],
    // navigate / logout from hooks are stable references — listing
    // them would be safe but adds noise; this project's eslint config
    // doesn't enforce exhaustive-deps so an empty array is fine.
    [],
  );

  // Debounce the query for the network-bound post search. 200ms is long
  // enough to skip most rapid typing, short enough to feel instant once
  // the user stops.
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  // Settings drive feature-flag gates on nav items (mirrors sidebar).
  // Fail-open on undefined inside navItemsFor.
  const settingsQuery = useQuery(settingsListQueryOptions());
  const settingsMap = useMemo(() => {
    if (!settingsQuery.data) return undefined;
    return new Map(settingsQuery.data.map((s) => [s.key, s.value]));
  }, [settingsQuery.data]);

  const navMatches = useMemo(() => {
    // Two-step: first scope to items this user can use (matches sidebar),
    // then narrow by the keyword query. No point letting an author "find"
    // a /roles entry that 403s on click.
    // Flatten parent groups → leaf entries so submenu items (Pages /
    // Group Content / Layout Templates under Pages Manager) stay
    // searchable by keyword without the user having to know they're
    // nested.
    const allowed = flattenNavItems(navItemsFor(user, ACTIVE_NAV, settingsMap)).filter(
      (item): item is typeof item & { to: string } => typeof item.to === 'string',
    );
    const q = query.trim().toLowerCase();
    if (!q) return allowed;
    return allowed.filter((item) => {
      const hay = `${item.label} ${item.keywords ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [query, user, settingsMap]);

  // Post search — gated on ≥ 2 chars to keep the first keystroke cheap and
  // avoid loading every post on an empty palette. perPage=8 so the menu
  // doesn't overflow the dropdown; admin can switch to /posts for more.
  // Disabled in action mode (`>...`) — actions shouldn't trigger a fetch.
  const postsQuery = useQuery({
    ...postsListQueryOptions({
      q: debounced,
      perPage: 8,
      sortBy: 'updatedAt',
      sortDir: 'desc',
    }),
    enabled: !actionMode && debounced.length >= 2,
    staleTime: 30_000,
  });
  const postMatches = postsQuery.data?.items ?? [];

  // Actions filtered for THIS user's perms / role. Always computed
  // even when not in action mode — same allowlist, cheap.
  const allowedActions = useMemo(() => {
    if (!user) return [] as Action[];
    return ACTIONS.filter((a) => {
      if (a.requiresRole && user.role.slug !== a.requiresRole) return false;
      if (a.requires && !hasPermission(user, a.requires)) return false;
      return true;
    });
  }, [ACTIONS, user]);

  const rows: Row[] = useMemo(() => {
    // Action mode — replace nav+post sections with the filtered action
    // list. Empty query after `>` shows everything available; non-empty
    // narrows by label + keyword substring.
    if (actionMode) {
      const matching = allowedActions.filter((a) => {
        if (!actionQuery) return true;
        const hay = `${a.label} ${a.keywords}`.toLowerCase();
        return hay.includes(actionQuery);
      });
      return matching.map((a) => ({
        kind: 'action',
        key: `action:${a.key}`,
        label: a.label,
        icon: a.icon,
        run: a.run,
      }));
    }
    const r: Row[] = navMatches.map((n) => ({
      kind: 'nav',
      key: `nav:${n.to}`,
      label: n.label,
      icon: n.icon,
      to: n.to,
    }));
    // Empty-query bonus: surface recently-viewed posts under the menu
    // so "where was I just?" is one keystroke away. Skipped once the
    // user starts typing — at that point the real post search takes
    // over and recents would just be noise.
    if (!query.trim()) {
      for (const recent of recents) {
        r.push({
          kind: 'post',
          key: `recent:${recent.id}`,
          label: recent.title,
          slug: recent.slug,
          icon: FileText,
          to: '/posts/$postId',
          params: { postId: recent.id },
        });
      }
    }
    for (const p of postMatches) {
      r.push({
        kind: 'post',
        key: `post:${p.id}`,
        label: p.title,
        slug: p.slug,
        icon: FileText,
        to: '/posts/$postId',
        params: { postId: p.id },
      });
    }
    return r;
  }, [actionMode, actionQuery, allowedActions, navMatches, postMatches, query, recents]);

  // Clamp selection when filter shrinks the list.
  useEffect(() => {
    if (selectedIdx >= rows.length) {
      setSelectedIdx(Math.max(0, rows.length - 1));
    }
  }, [rows.length, selectedIdx]);

  const goRow = (row: Row) => {
    setOpen(false);
    if (row.kind === 'post') {
      void navigate({ to: row.to, params: row.params });
    } else if (row.kind === 'action') {
      row.run();
    } else {
      void navigate({ to: row.to });
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIdx((i) => Math.min(rows.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const row = rows[selectedIdx];
      if (row) goRow(row);
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={setOpen}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <DialogPrimitive.Content
          className="data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 fixed left-1/2 top-[20%] z-50 w-[92vw] max-w-lg -translate-x-1/2 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900"
          onKeyDown={onKeyDown}
        >
          <DialogPrimitive.Title className="sr-only">Command palette</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            ค้นหาและไปยังหน้าต่างๆ — Arrow keys + Enter
          </DialogPrimitive.Description>

          <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
            <Search className="h-4 w-4 shrink-0 text-slate-400" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelectedIdx(0);
              }}
              placeholder="ค้นหาเมนูหรือบทความ... (พิมพ์ > สำหรับ actions)"
              className="flex-1 bg-transparent text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none dark:text-slate-100"
            />
            <kbd className="hidden rounded border border-slate-200 px-1.5 py-0.5 font-mono text-[10px] text-slate-500 sm:inline dark:border-slate-700 dark:text-slate-400">
              ESC
            </kbd>
          </div>

          <div
            ref={listRef}
            className="max-h-[60vh] overflow-y-auto p-1"
            role="listbox"
            aria-label="Command palette results"
          >
            {rows.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">
                {actionMode
                  ? 'ไม่พบ action'
                  : debounced.length >= 2 && postsQuery.isFetching
                    ? 'กำลังค้นหา...'
                    : 'ไม่พบรายการ'}
              </div>
            ) : (
              <>
                {actionMode && <SectionHeader>Actions</SectionHeader>}
                {!actionMode && navMatches.length > 0 && <SectionHeader>เมนู</SectionHeader>}
                {rows.map((row, idx) => {
                  const Icon = row.icon;
                  const selected = idx === selectedIdx;
                  // Header label switches based on context: "ล่าสุดที่เปิด"
                  // when query is empty (recents); "บทความ" when the user
                  // is actively searching.
                  const showPostsHeader =
                    row.kind === 'post' && (idx === 0 || rows[idx - 1]?.kind !== 'post');
                  const postsHeaderLabel = !query.trim() ? 'ล่าสุดที่เปิด' : 'บทความ';
                  return (
                    <div key={row.key}>
                      {showPostsHeader && <SectionHeader>{postsHeaderLabel}</SectionHeader>}
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onMouseEnter={() => setSelectedIdx(idx)}
                        onClick={() => goRow(row)}
                        className={cn(
                          'flex w-full items-center gap-3 rounded px-3 py-2 text-left text-sm transition-colors',
                          selected
                            ? 'bg-brand-50 text-brand-900 dark:bg-brand-900/30 dark:text-brand-100'
                            : 'text-slate-700 dark:text-slate-200',
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0 text-slate-400" />
                        <span className="flex-1 truncate">{row.label}</span>
                        {row.kind === 'post' && (
                          <span className="truncate font-mono text-[10px] text-slate-400">
                            /{row.slug}
                          </span>
                        )}
                        {selected && (
                          <kbd className="rounded border border-slate-200 px-1.5 py-0.5 font-mono text-[10px] text-slate-500 dark:border-slate-700 dark:text-slate-400">
                            ↵
                          </kbd>
                        )}
                      </button>
                    </div>
                  );
                })}
                {debounced.length >= 2 && postsQuery.isFetching && (
                  <div className="px-3 py-2 text-xs text-slate-400">กำลังค้นหาบทความ...</div>
                )}
              </>
            )}
          </div>

          <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-2 text-[11px] text-slate-500 dark:border-slate-800 dark:bg-slate-800/40 dark:text-slate-400">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-slate-200 px-1 py-0.5 font-mono dark:border-slate-700">
                  ↑↓
                </kbd>{' '}
                เลื่อน
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-slate-200 px-1 py-0.5 font-mono dark:border-slate-700">
                  ↵
                </kbd>{' '}
                เปิด
              </span>
            </div>
            <span className="flex items-center gap-1">
              <Command className="h-3 w-3" />K เปิด/ปิด
            </span>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
      {children}
    </div>
  );
}
